import React, { useState, useMemo, useEffect, useRef,useContext } from 'react';
import {
  Box,
  Grid,
  Typography,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Divider,
  Chip
} from '@mui/material';
import {
  format,
  addDays,
  isSameDay,
  subDays,
  isWithinInterval,
  differenceInDays,
  parseISO,
  startOfDay,
  endOfDay,
} from 'date-fns';
import moment from 'moment';
import LegendFilterGroup from '../../../components/UIComponents/LegendFilterGroup';
import CustomCard from '../../../components/UIComponents/CustomCard';
import StyledTextField from '../../../components/UIComponents/StyledTextField';
import CustomTypography from '../../../components/UIComponents/CustomTypography';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GridViewIcon from '@mui/icons-material/GridView';
import { fetchReservationList, fetchRoomList, fetchRoomTypes } from '../../../service/network';
import { useNavigate } from 'react-router-dom';
import HeaderComponent from '../../../components/Header';
import { Close, ConfirmationNumber, FiberManualRecord, Launch } from '@mui/icons-material';
import { AuthContext } from '../../../context/auth.context';

const sectionColors = {
  'Standard': '#FFE7B2',
  'Deluxe': '#BCD4FF',
  'Executive': '#E4C4FF',
};

function darkenColor(color, percent) {
  if (color.startsWith('#')) {
    const r = parseInt(color.substr(1, 2), 16);
    const g = parseInt(color.substr(3, 2), 16);
    const b = parseInt(color.substr(5, 2), 16);

    const darken = (val) => Math.max(0, val - Math.round(val * (percent / 100)));

    return `#${[r, g, b].map(darken).map(v => v.toString(16).padStart(2, '0')).join('')}`;
  }
  return color;
}

const getDateRange = (from, to) => {
  const range = [];
  let current = new Date(from);
  const endDate = new Date(to);
  while (current <= endDate) {
    range.push(new Date(current));
    current = addDays(current, 1);
  }
  return range;
};
const paymentStatusStyles = {
  'Paid': { bg: 'rgba(0, 184, 148, 0.2)', border: 'rgba(0, 184, 148, 0.5)', text: '#00B894' },
  'Unpaid': { bg: 'rgba(255, 94, 94, 0.2)', border: 'rgba(255, 94, 94, 0.5)', text: '#FF5E5E' },
  'Partially Paid': { bg: 'rgba(251, 192, 45, 0.2)', border: 'rgba(251, 192, 45, 0.5)', text: '#FBC02D' },
  'default': { bg: 'rgba(204, 204, 204, 0.2)', border: 'rgba(204, 204, 204, 0.5)', text: '#666' }
};

const statusStyles = {
  'Booked': { bg: 'rgba(38, 105, 239, 0.2)', border: 'rgba(38, 105, 239, 0.5)', text: '#2669EF' },
  'Checked-In': { bg: 'rgba(0, 182, 155, 0.2)', border: 'rgba(0, 182, 155, 0.5)', text: '#00B69B' },
  'Checked-Out': { bg: 'rgba(255, 59, 48, 0.2)', border: 'rgba(255, 59, 48, 0.5)', text: '#FF3B30' },
  'Cancelled': { bg: 'rgba(239, 83, 80, 0.2)', border: 'rgba(239, 83, 80, 0.5)', text: '#EF5350' }, // Added Cancelled status
  'default': { bg: 'rgba(158, 158, 158, 0.2)', border: 'rgba(158, 158, 158, 0.5)', text: '#9E9E9E' }
};
const getCardColorByDate = (date, status) => {
  // First check if status is 'Cancelled'
  if (status === 'Cancelled') {
    return { bg: '#FFCDD2', border: '#EF5350' }; // Red colors for cancelled bookings
  }

  const today = moment().startOf('day');
  const bookingDate = moment(date).startOf('day');

  if (bookingDate.isBefore(today)) {
    return { bg: '#F0F0F0', border: '#B0B0B0' }; // Past
  } else if (bookingDate.isSame(today)) {
    return { bg: '#FFF9C4', border: '#FBC02D' }; // Today
  } else {
    return { bg: '#E0F7FA', border: '#00ACC1' }; // Upcoming
  }
};

const groupRoomsByType = (rooms) => {
  return rooms.reduce((acc, room) => {
    acc[room.type] = acc[room.type] || [];
    acc[room.type].push(room);
    return acc;
  }, {});
};
const getBookingForDate = (bookings = [], date) => {
  return bookings.find(b => {
    const bookingStart = new Date(b.from);
    const bookingEnd = new Date(b.to);
    const currentDate = new Date(date);

    // Check if current date is within booking range
    return currentDate >= bookingStart && currentDate <= bookingEnd;
  });
};
const calculateReservationWidth = (
  booking,
  date,
  cellWidth,
  calendarFrom,
  calendarTo,
  overlappingIndex = 0
) => {
  if (!booking) return { width: 0, offset: 0, verticalOffset: 0 };

  const bookingStart = startOfDay(new Date(booking.from));
  const bookingEnd = startOfDay(new Date(booking.to));
  const currentDate = startOfDay(new Date(date));
  const rangeStart = startOfDay(new Date(calendarFrom));
  const rangeEnd = startOfDay(new Date(calendarTo));

  // Adjusted bookingEnd to exclude the end date
  const adjustedBookingEnd = new Date(bookingEnd);
  adjustedBookingEnd.setDate(adjustedBookingEnd.getDate() - 0.8);

  // Vertical offset for overlapping reservations
  const verticalOffset = overlappingIndex * 0; // Adjust as needed for overlap
  const horizontalOffset = cellWidth * 1; // Optional offset

  if (currentDate < bookingStart) {
    return { width: 0, offset: 0, verticalOffset: 0 };
  }

  if (bookingStart < rangeStart && isSameDay(currentDate, rangeStart)) {
    const visibleDays = differenceInDays(
      Math.min(adjustedBookingEnd, rangeEnd),
      rangeStart
    ) + 1;
    return {
      width: visibleDays * cellWidth,
      offset: horizontalOffset,
      verticalOffset
    };
  }

  if (isSameDay(currentDate, bookingStart)) {
    const daysInBooking = differenceInDays(
      Math.min(adjustedBookingEnd, rangeEnd),
      bookingStart
    ) + 1;
    return {
      width: daysInBooking * cellWidth,
      offset: horizontalOffset,
      verticalOffset
    };
  }

  return { width: 0, offset: 0, verticalOffset: 0 };
};

export default function CalendarView({ onSwitchToGrid }) {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [fromDate, setFromDate] = useState(() => moment().subtract(7, 'days').startOf('day').toDate());
  const [toDate, setToDate] = useState(() => moment().add(7, 'days').endOf('day').toDate());

  const [dateRange, setDateRange] = useState(getDateRange(fromDate, toDate));
  const [selectedType, setSelectedType] = useState('All');
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [roomTypes, setRoomTypes] = useState([]);
  const [roomTypeColors, setRoomTypeColors] = useState({});
    const { selLocation } = useContext(AuthContext);

  const handleOpenDialog = (booking) => {
    setSelectedBooking(booking);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedBooking(null);
  };

  useEffect(() => {
    setDateRange(getDateRange(fromDate, toDate));
  }, [fromDate, toDate]);
useEffect(() => {
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch reservations
      const reservationParams = {
        doctype: 'Reservation',
        fields: JSON.stringify([
          "name", "primary_guest_name", "from_date", "to_date",
          "booking_source", "status", "room_type", "room", "room_name", "payment_status"
        ]),
        filters: JSON.stringify([
          ["to_date", ">=", format(fromDate, 'yyyy-MM-dd')],
          ["from_date", "<=", format(toDate, 'yyyy-MM-dd')]
        ])
      };

      // Fetch all rooms
      const roomParams = {
        doctype: 'Room',
        fields: JSON.stringify(["room_name", "type", "status", "name"]),
        filters: JSON.stringify({ company: selLocation[0] }) // Add your location filter if needed
      };

      const [reservationsResponse, roomsResponse] = await Promise.all([
        fetchReservationList(reservationParams),
        fetchRoomList(roomParams)
      ]);

      const reservations = reservationsResponse.data.message?.values || [];
      const allRooms = roomsResponse.data.message?.values || [];

      // Create a map of all rooms first
      const roomMap = {};
      
      // Initialize all rooms with default data
      allRooms.forEach(room => {
        const [roomName, roomType, status, roomId] = room;
        roomMap[roomId] = {
          id: roomId,
          name: roomName,
          type: roomType,
          status: status || 'Vacant', // Use room's status if available
          bookings: [],
          paymentstatus: null
        };
      });

      // Add reservations to the rooms
      if (Array.isArray(reservations)) {
        reservations.forEach(reservation => {
          const [reservationId, guestName, fromDate, toDate, , status, roomType, roomId, roomName, paymentStatus] = reservation;
          
          if (!roomMap[roomId]) {
            // If room not in our master list, add it
            roomMap[roomId] = {
              id: roomId,
              name: roomName,
              type: roomType,
              status: 'Vacant',
              bookings: [],
              paymentstatus: paymentStatus
            };
          }

          roomMap[roomId].bookings.push({
            from: parseISO(fromDate),
            to: parseISO(toDate),
            status: status,
            reservationId,
            guestName,
            roomType,
            roomName,
            paymentStatus
          });

          // Update room status based on current booking if needed
          const today = new Date();
          if (isWithinInterval(today, { start: parseISO(fromDate), end: parseISO(toDate) })) {
            roomMap[roomId].status = status || 'Occupied';
          }
        });
      }

      const roomsArray = Object.values(roomMap);
      setRooms(roomsArray);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [fromDate, toDate, selLocation]);
  const loadRoomTypes = async () => {
    try {
      const params = {
        doctype: 'Room Type',
        fields: JSON.stringify(["name", "room_type", "colour"])
      };
      const res = await fetchRoomTypes(params);

      const roomColorMap = {};
      res.data.message.values.forEach(item => {
        const roomType = item[1]; // room_type
        const color = item[2] || '#f5f5f5'; // fallback color
        roomColorMap[roomType] = color;
      });

      const uniqueRoomTypes = [...new Set(res.data.message.values.map(item => item[1]))];
      const formattedRoomTypes = [
        { label: 'All', value: 'All' },
        ...uniqueRoomTypes.map(type => ({ label: type, value: type }))
      ];

      setRoomTypes(formattedRoomTypes);
      setRoomTypeColors(roomColorMap);
    } catch (err) {
      console.error("Failed to fetch room types", err);
      setRoomTypes([
        { label: 'All', value: 'All' },
        { label: 'Standard', value: 'Standard' },
        { label: 'Deluxe', value: 'Deluxe' },
        { label: 'Executive', value: 'Executive' }
      ]);
      setRoomTypeColors({
        'Standard': '#FFE7B2',
        'Deluxe': '#BCD4FF',
        'Executive': '#E4C4FF'
      });
    }
  };

  useEffect(() => {
    loadRoomTypes();

  }, []);

  const handleFromDateChange = (e) => {
    const newFromDate = new Date(e.target.value);
    const newToDate = addDays(newFromDate, 14); // 14 days added to make it 15 days total (including start day)
    setFromDate(newFromDate);
    setToDate(newToDate);
  };

  const handleToDateChange = (e) => {
    const newToDate = new Date(e.target.value);
    const newFromDate = subDays(newToDate, 14); // 14 days subtracted to make it 15 days total (including end day)
    setFromDate(newFromDate);
    setToDate(newToDate);
  };
  const filteredRooms = rooms.filter(room =>
    selectedType === 'All' || room.type === selectedType
  );

  const getCellWidth = () => {
    if (isLargeScreen) return 100;
    if (isMediumScreen) return 80;
    if (isSmallScreen) return 60;
    return 70;
  };

  const cellWidth = getCellWidth();
  const groupedRooms = useMemo(() => groupRoomsByType(filteredRooms), [filteredRooms]);

  const handlenavigatedetails = (reservationId) => {
    navigate(`/reservation`, {
      state: { reservationId: reservationId }
    });
  };
// Add this helper function at the top of your component
const isValidDate = (date) => {
  return date instanceof Date && !isNaN(date);
};

// Safe format function
const safeFormat = (date, formatString, fallback = 'N/A') => {
  if (!date || !isValidDate(new Date(date))) return fallback;
  try {
    return format(new Date(date), formatString);
  } catch (error) {
    console.error('Date formatting error:', error);
    return fallback;
  }
};

  return (
    <Box sx={{
      width: '100%',
      height: '100vh',
      p: { xs: 0.5, sm: 1, md: 2 },

    }}>
      <HeaderComponent title="FrontDesk" />

      <CustomCard
        bgColor="light"
        shadow="lg"
        borderRadius="xxl"
        sx={{
          width: '100%',
          height: '100%',
          borderTopLeftRadius: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          position="relative"
          display="flex"
          justifyContent="center"
          alignItems="center"
          px={2}
          minHeight="60px"
          flexShrink={0}
        >
          <CustomTypography
            variant="HeadingLarge"
            color="dark"
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              textAlign: 'center',
            }}
          >
            Calendar View
          </CustomTypography>

          <Box
            position="absolute"
            right={16}
            display="flex"
            alignItems="center"
            gap={1}
            mr={1}
          >
            <ToggleButtonGroup
              value="calendar" // Always selected
              exclusive
              onChange={(e, newView) => {
                if (newView === 'grid') {
                  onSwitchToGrid(); // Switch back to grid view
                }
              }}
              sx={{
                backgroundColor: '#f2f2f2',
                borderRadius: '32px',
                overflow: 'hidden',
                boxShadow: '0px 4px 12px 0px #4A4C4E40',
                '& .MuiToggleButton-root': {
                  border: 0,
                  borderRadius: '0 !important',
                  padding: '1px 15px',
                  color: '#000',
                  backgroundColor: 'light.main',
                  '&.Mui-selected': {
                    backgroundColor: 'secondary.main',
                  },
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                },
              }}
            >
              <ToggleButton value="grid">
                <Tooltip title="Grid View">
                  <GridViewIcon sx={{ width: "20px" }} />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="calendar" selected>
                <Tooltip title="Calendar View">
                  <CalendarMonthIcon sx={{ width: "20px" }} />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        <Box px={{ xs: 1, sm: 2 }} mt={2} flexShrink={0}>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            gap={2}
          >
            <Box flex={1} minWidth="280px" maxWidth="350px" >
              <LegendFilterGroup
                title="Room Type"
                items={roomTypes}
                selected={selectedType}
                onSelect={(value) => setSelectedType(value)}
                showCount={false}
                columns={4}
                align="center"
                getLabel={(item) => item.label}
                getValue={(item) => item.value}
              />
            </Box>

            <Box
              display="flex"
              gap={2}
              flexWrap="wrap"
              justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
              flexGrow={1}
              mr={1}
              alignItems="center"
              width={{ xs: '100%', sm: 'auto' }}
            >
              <Grid item xs={12} sm={5} md={4}>
               <StyledTextField
  label="From Date"
  type="date"
  fullWidth
  size="small"
  value={safeFormat(fromDate, 'yyyy-MM-dd')}
  onChange={handleFromDateChange}
  InputLabelProps={{ shrink: true }}
  inputProps={{
    max: safeFormat(toDate, 'yyyy-MM-dd')
  }}
/>
              </Grid>
              <Grid item xs={12} sm={5} md={4}>
             <StyledTextField
  label="To Date"
  type="date"
  fullWidth
  size="small"
  value={safeFormat(toDate, 'yyyy-MM-dd')}
  onChange={handleToDateChange}
  InputLabelProps={{ shrink: true }}
  inputProps={{
    min: safeFormat(fromDate, 'yyyy-MM-dd')
  }}
/>
              </Grid>
            </Box>
          </Box>
        </Box>

        <Box
          ref={scrollContainerRef}
          sx={{
            flexGrow: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box sx={{
            overflow: 'auto',
            flexGrow: 1,
            position: 'relative'
          }}>
            <Box sx={{
              minWidth: 'fit-content',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0
            }}>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                py={2}
                my={2}
                pl={1}
                sx={{
                  backgroundColor: '#e0ecf8',
                  borderRadius: 2,
                  boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
                  width: '100%',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                }}
              >
                <Box

                  minWidth={200}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"


                  sx={{
                    position: 'sticky',
                    zIndex: 3,
                    backgroundColor: '#4d82b8',
                    color: 'white',
                    borderRadius: 2,
                    fontWeight: 600,
                    height: 56,
                    flexShrink: 0,
                    mb: 1,
                    fontSize: 14

                  }}
                >
  {safeFormat(fromDate, 'MMM dd, yyyy')} - {safeFormat(toDate, 'MMM dd, yyyy')}
                </Box>

                <Box
                  display="flex"
                  gap={1}
                  sx={{
                    paddingBottom: 1,
                    paddingRight: 2,
                    flexGrow: 1,
                    position: 'relative',
                  }}
                >
                  {dateRange.map((date, index) => {
                    const isToday = isSameDay(date, new Date());
                    return (
                      <Box
                        key={date.toISOString()}
                        sx={{
                          width: cellWidth,
                          minWidth: cellWidth,
                          borderRadius: 2,
                          overflow: 'hidden',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                          flexShrink: 0,
                          position: 'relative',
                        }}
                      >


                        <Box
                          sx={{
                            backgroundColor: isToday ? '#2f4c6e' : '#7aa7d9',
                            color: '#fff',
                            textAlign: 'center',
                            py: 0.5,
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {format(date, 'EEE')}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            backgroundColor: isToday ? 'secondary.main' : '#d8e8f8',
                            color: '#000',
                            textAlign: 'center',
                            py: 0.5,
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {format(date, 'dd')}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              {Object.entries(groupedRooms).map(([roomType, rooms]) => {
                const roomTypeColor = roomTypeColors[roomType] || '#f5f5f5';
                const columnBgColor = `${roomTypeColor}33`; // add transparency
                const slightlyDarkerColor = darkenColor(roomTypeColor, 10);


                return (
                  <Box key={roomType} sx={{ mb: 4 }}>
                    <Box
                      sx={{
                        backgroundColor: slightlyDarkerColor,
                        px: 0.5,
                        py: 0.5,
                        position: 'sticky',
                        left: 0,
                        zIndex: 5,
                        borderTopRightRadius: '12px',
                        borderBottomRightRadius: '12px',
                        boxShadow: 'inset 0 -1px 0 #ccc',
                        mb: 1,
                        minWidth: 'fit-content',
                      }}
                    >
                      <Typography fontWeight={700} textAlign="center" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                        {roomType} Room
                      </Typography>
                    </Box>

                    {rooms.map((room) => (
                      <Box key={room.id} sx={{
                        display: 'flex',
                        mb: 1,
                        background: `repeating-linear-gradient(
                          to right,
                          ${columnBgColor},
                          ${columnBgColor} ${cellWidth}px,
                          transparent ${cellWidth}px,
                          transparent ${cellWidth}px
                        )`,
                      }}>
                        <Box
                          minWidth={180}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"

                          sx={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 3,
                            backgroundColor: slightlyDarkerColor,
                            boxShadow: '2px 0 6px rgba(0,0,0,0.05)',

                            borderRadius: "12px",
                            fontWeight: 600,
                            height: 28,
                            ml: 1,
                          }}
                        >
                          <Typography variant='bodyText1' sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}> {roomType} - {room.name}</Typography>
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            minWidth: `${cellWidth * dateRange.length}px`,
                            flexShrink: 0,

                          }}
                        >
                          {dateRange.map((date, idx) => {
                            // Get all bookings for this date
                            const bookingsForDate = room.bookings.filter(b => {
                              const bookingStart = new Date(b.from);
                              const bookingEnd = new Date(b.to);
                              return date >= bookingStart && date <= bookingEnd;
                            });

                            // Sort bookings by start date
                            const sortedBookings = [...bookingsForDate].sort((a, b) =>
                              new Date(a.from) - new Date(b.from)
                            );

                            return (
                              <Box
                                key={idx}
                                sx={{
                                  width: cellWidth,
                                  minWidth: cellWidth,
                                  height: 28,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  position: 'relative',
                                  flexShrink: 0,
                                }}
                              >
                                {sortedBookings.map((booking, bookingIndex) => {
                                  const isFirstDayOfBooking = isSameDay(date, booking.from);
                                  const bookingStartsBeforeRange = new Date(booking.from) < new Date(fromDate);
                                  const isFirstDayOfRange = idx === 0;

                                  const shouldRenderBooking =
                                    (isFirstDayOfBooking) ||
                                    (bookingStartsBeforeRange && isFirstDayOfRange);

                                  const { width, offset, verticalOffset } = calculateReservationWidth(
                                    booking,
                                    date,
                                    cellWidth,
                                    fromDate,
                                    toDate,
                                    bookingIndex
                                  );

                                  const cardColor = getCardColorByDate(date, booking.status);
                                  return (
                                    shouldRenderBooking && width > 0 && (
                                      <Box
                                        key={`${booking.reservationId}-${idx}`}
                                        onClick={() => handleOpenDialog(booking)}
                                        sx={{
                                          width: `${width}px`,
                                          height: '24px',
                                          backgroundColor: cardColor.bg,
                                          border: `2px solid ${cardColor.border}`,
                                          borderRadius: 2,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          position: 'absolute',
                                          left: `${offset}px`, // This now includes the 0.5 cell offset
                                          top: `${verticalOffset}px`,
                                          zIndex: 2 + bookingIndex,
                                          overflow: 'hidden',
                                          boxSizing: 'border-box',
                                          pl: 1,
                                          pr: 0.5,
                                          cursor: 'pointer',
                                          '&:hover': {
                                            opacity: 0.9,
                                            boxShadow: '0 0 8px rgba(0,0,0,0.2)',
                                            zIndex: 10
                                          }
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          fontWeight={600}
                                          noWrap
                                          sx={{
                                            fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                            color: '#333',
                                            textOverflow: 'ellipsis',
                                            overflow: 'hidden',
                                            flexGrow: 1,
                                          }}
                                        >
                                          {booking.reservationId}
                                        </Typography>
                                        <Box
                                          width={12}
                                          height={12}
                                          borderRadius="50%"
                                          border={`2px solid ${cardColor.border}`}
                                          bgcolor="white"
                                          flexShrink={0}
                                          mr={0.5}
                                        />
                                      </Box>
                                    )
                                  );
                                })}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </CustomCard>

      {/* Reservation Details Dialog */}
      {/* Reservation Details Dialog */}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm" // Changed from "sm" to "md" for more width
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            background: 'linear-gradient(145deg, #f5f9ff, #fefeff)',
            overflow: 'hidden',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.1)',
            transform: openDialog ? 'scale(1)' : 'scale(0.95)',
            opacity: openDialog ? 1 : 0,
            border: '1px solid rgba(255, 255, 255, 0.3)',
            mx: 2, // Add horizontal margin for small screens
            width: 'calc(100% - 32px)', // Adjust width to account for margins
            maxHeight: '90vh' // Ensure it doesn't touch screen edges vertically
          }
        }}
        TransitionProps={{
          timeout: { enter: 200, exit: 150 }
        }}
      >
        {selectedBooking && (
          <>
            <DialogTitle
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                color: 'white',
                py: 2.5,
                px: 4,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '1.3rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  height: '4px',
                  background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.light})`
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ConfirmationNumber sx={{ mr: 1.5, fontSize: '1.8rem' }} />
                <Typography variant="h6" fontWeight="800" sx={{
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  letterSpacing: '0.5px'
                }}>
                  RESERVATION DETAILS
                </Typography>
              </Box>
              <IconButton
                onClick={handleCloseDialog}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.25)',
                    transform: 'rotate(90deg)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{
              p: { xs: 2, sm: 3, md: 4 }, // Responsive padding
              mt: 2,
              overflowY: 'auto',
              '&.MuiDialogContent-root': {
                padding: { xs: 2, sm: 3, md: 4 } // Ensure consistent padding
              }
            }}>
              {/* First Row: Reservation ID, Room Type, Guest Name */}
              <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
                <Grid size={6}>
                  <Box sx={{
                    p: { xs: 1.5, sm: 2.5 },
                    borderRadius: '12px',
                    background: 'linear-gradient(145deg, rgba(25, 118, 210, 0.08), rgba(25, 118, 210, 0.04))',
                    borderLeft: '5px solid',
                    borderColor: theme.palette.primary.main,
                    transition: 'all 0.4s ease',
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.05)',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 20px rgba(25, 118, 210, 0.15)'
                    }
                  }}>
                    <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{
                      display: 'block',
                      mb: 0.5,
                      letterSpacing: '0.5px'
                    }}>
                      RESERVATION ID
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative'
                    }}>
                      <Typography
                        component="a"
                        onClick={() => handlenavigatedetails(selectedBooking.reservationId)}
                        sx={{
                          color: theme.palette.primary.dark,
                          fontWeight: 800,
                          textDecoration: 'none',
                          fontSize: '1.1rem',
                          letterSpacing: '0.5px',
                          cursor: 'pointer',
                          '&:hover': {
                            color: theme.palette.primary.main,
                            '&::before': {
                              width: '100%'
                            }
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '0%',
                            height: '2px',
                            backgroundColor: theme.palette.primary.main,
                            transition: 'width 0.4s ease'
                          }
                        }}
                      >
                        {selectedBooking.reservationId}
                      </Typography>
                      <Launch sx={{
                        fontSize: '1rem',
                        ml: 1,
                        color: theme.palette.primary.main,
                        transition: 'all 0.3s ease',
                        'a:hover &': {
                          transform: 'translateX(3px) rotate(-45deg)'
                        }
                      }} />
                    </Box>
                    <DetailItem
                      label="ROOM No."
                      value={selectedBooking.roomName}
                      sx={{
                        p: { xs: 1.5, sm: 2.5 },
                        borderRadius: '12px',
                        background: 'linear-gradient(145deg, rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0.01))',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        height: '100%',
                        '& .MuiTypography-body1': {
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          fontSize: '1.05rem'
                        }
                      }}
                    />
                  </Box>
                </Grid>

                <Grid size={3}>
                  <DetailItem
                    label="ROOM TYPE"
                    value={selectedBooking.roomType}
                    sx={{
                      p: { xs: 1.5, sm: 2.5 },
                      borderRadius: '12px',
                      background: 'linear-gradient(145deg, rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0.01))',
                      border: '1px solid rgba(0, 0, 0, 0.05)',
                      height: '100%',
                      '& .MuiTypography-body1': {
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontSize: '1.05rem'
                      }
                    }}
                  />
                </Grid>

                <Grid size={3}>
                  <DetailItem
                    label="GUEST NAME"
                    value={selectedBooking.guestName}
                    sx={{
                      p: { xs: 1.5, sm: 2.5 },
                      borderRadius: '12px',
                      background: 'linear-gradient(145deg, rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0.01))',
                      border: '1px solid rgba(0, 0, 0, 0.05)',
                      height: '100%',
                      '& .MuiTypography-body1': {
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontSize: '1.05rem'
                      }
                    }}
                  />
                </Grid>
              </Grid>

              {/* Second Row: Check-In, Check-Out, Duration */}
              <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
                <Grid size={5}>
                  <DetailItem
                    label="CHECK-IN"
                    value={
                      <Box>
                         <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
        {safeFormat(selectedBooking.from, 'EEE, MMM dd, yyyy', 'Invalid date')}
      </Typography>
                      </Box>
                    }
                    sx={{
                      p: { xs: 1.5, sm: 2.5 },
                      borderRadius: '12px',
                      background: 'linear-gradient(145deg, rgba(46, 125, 50, 0.05), rgba(46, 125, 50, 0.02))',
                      borderLeft: '5px solid',
                      borderColor: theme.palette.success.main,
                      height: '100%'
                    }}
                  />
                </Grid>

                <Grid size={5}>
                  <DetailItem
                    label="CHECK-OUT"
                    value={
                      <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
        {safeFormat(selectedBooking.to, 'EEE, MMM dd, yyyy', 'Invalid date')}
      </Typography>
                      </Box>
                    }
                    sx={{
                      p: { xs: 1.5, sm: 2.5 },
                      borderRadius: '12px',
                      background: 'linear-gradient(145deg, rgba(211, 47, 47, 0.05), rgba(211, 47, 47, 0.02))',
                      borderLeft: '5px solid',
                      borderColor: theme.palette.error.main,
                      height: '100%'
                    }}
                  />
                </Grid>

                <Grid size={2}>
                  <DetailItem
                    label="DURATION"
                    value={
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                      }}>
                        <Typography variant="h5" fontWeight="800" color={theme.palette.primary.dark}>
                          {differenceInDays(selectedBooking.to, selectedBooking.from)}
                        </Typography>
                        <Typography variant="body1" fontWeight="600" color="text.secondary">
                          NIGHTS
                        </Typography>
                      </Box>
                    }
                    sx={{
                      p: { xs: 1, sm: 2 },
                      borderRadius: '12px',
                      background: 'linear-gradient(145deg, rgba(25, 118, 210, 0.05), rgba(25, 118, 210, 0.02))',
                      border: '1px dashed rgba(25, 118, 210, 0.3)',
                      textAlign: 'center',
                      height: '100%'
                    }}
                  />
                </Grid>
              </Grid>

              {/* Third Row: Status Indicators */}
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                {/* Booking Status */}
                <Grid size={6}>

                  <Box sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: '12px',
                    background: 'linear-gradient(145deg, rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0.01))',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '5px',
                      height: '100%',
                      background: statusStyles[selectedBooking.status]?.border
                    }
                  }}>
                    <Typography variant="subtitle2" fontWeight="800" color="textSecondary" sx={{
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      letterSpacing: '0.5px'
                    }}>
                      <FiberManualRecord sx={{
                        fontSize: '0.8rem',
                        mr: 1,
                        color: statusStyles[selectedBooking.status]?.border
                      }} />
                      BOOKING STATUS
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <Typography variant="body1" fontWeight="600" sx={{
                        color: statusStyles[selectedBooking.status]?.text,
                        ml: 2
                      }}>
                        {selectedBooking.status.toUpperCase()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Payment Status */}
                <Grid size={6}>

                  <Box sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: '12px',
                    background: 'linear-gradient(145deg, rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0.01))',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '5px',
                      height: '100%',
                      background: paymentStatusStyles[selectedBooking.paymentStatus]?.border
                    }
                  }}>
                    <Typography variant="subtitle2" fontWeight="800" color="textSecondary" sx={{
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      letterSpacing: '0.5px'
                    }}>
                      <FiberManualRecord sx={{
                        fontSize: '0.8rem',
                        mr: 1,
                        color: paymentStatusStyles[selectedBooking.paymentStatus]?.border
                      }} />
                      PAYMENT STATUS
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <Typography variant="body1" fontWeight="600" sx={{
                        color: paymentStatusStyles[selectedBooking.paymentStatus]?.text,
                        ml: 2
                      }}>
                        {(selectedBooking.paymentStatus || 'PENDING').toUpperCase()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{
              px: { xs: 2, sm: 4 },
              py: 3,
              borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              background: 'linear-gradient(to right, rgba(245, 245, 245, 0.8), rgba(250, 250, 250, 0.5))'
            }}>
              <Button
                onClick={handleCloseDialog}
                variant="contained"
                color="primary"
                sx={{
                  borderRadius: '12px',
                  px: 5,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 800,
                  letterSpacing: '0.5px',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  boxShadow: '0 4px 14px rgba(25, 118, 210, 0.4)',
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.1)',
                  position: 'relative',
                  overflow: 'hidden',
                  width: '100%',
                  maxWidth: '400px',
                  mx: 'auto'
                }}
              >
                CLOSE DETAILS
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

function DetailItem({ label, value }) {
  return (
    <Box mb={1}>
      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 'bold' }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  );
}